<template>
  <base-drawer
    class="audience-customers-drawer"
    :show="show"
    :title="reach ? 'Customers who will be reached' : 'Recipients'"
    width="640px"
    @close="onClose"
  >
    <drawer-section :title="sectionTitle" icon="icon-single-02">
      <template #header-actions>
        <base-button
          v-if="hasDownloadPermission && total > 0"
          icon
          class="btn-link text-white"
          title="Download as CSV"
          :disabled="downloading"
          @click="downloadCSV"
        >
          <i v-if="downloading" class="el-icon-loading"></i>
          <i v-else class="tim-icons icon-cloud-download-93"></i>
        </base-button>
      </template>

      <info-hint>
        {{ reach ? 'Each customer is emailed once, on their own day. This list is everyone with a birthday on file who matches right now; it is recalculated every day.' : 'Recipients are recalculated when the campaign sends, so this list reflects who matches right now.' }} Addresses marked undeliverable are skipped at send time and listed in the campaign report with the reason.
      </info-hint>

      <segmented-toggle
        class="mt-3"
        :value="mode"
        variant="pills"
        :options="[
          { value: 'all', label: 'All' },
          { value: 'deliverable', label: 'Deliverable' },
          { value: 'undeliverable', label: 'Undeliverable' }
        ]"
        @input="mode = $event"
      />

      <div v-if="loading && !customers.length" class="text-center py-4">
        <i class="el-icon-loading"></i>
      </div>

      <div v-else-if="error" class="py-3">
        <info-hint variant="danger" icon="icon-alert-circle-exc">{{ error }}</info-hint>
      </div>

      <div v-else-if="!customers.length" class="text-center py-4">
        <img src="/img/pandas/empty-transactions.png" alt="No recipients" class="empty-state-img empty-state-img--sm" />
        <h4 class="empty-state-text">{{ emptyText }}</h4>
      </div>

      <div v-else class="recipient-list mt-3">
        <div v-for="customer in customers" :key="customer.end_user_id + ':' + customer.email" class="recipient-row">
          <router-link
            v-if="customer.end_user_id"
            class="recipient-row__name text-white"
            :to="{ path: '/customers/details', query: { id: customer.end_user_id } }"
            target="_blank"
          >
            {{ displayName(customer) }}
          </router-link>
          <span v-else class="recipient-row__name text-white">{{ displayName(customer) }}</span>
          <div class="recipient-row__meta">
            <span class="text-white"><i class="tim-icons icon-email-85"></i>{{ customer.email }}</span>
            <span v-if="customer.phone" class="text-white"><i class="tim-icons el-icon-mobile-phone"></i>{{ formatPhoneDisplay(customer.phone, customer.country || 'US') }}</span>
            <span v-if="customer.undeliverable_reason" class="badge badge-warning recipient-row__flag">{{ reasonLabel(customer.undeliverable_reason) }}</span>
          </div>
        </div>
      </div>

      <div v-if="hasMore" class="text-center mt-2">
        <base-button variant="outlined" tone="primary" size="sm" :disabled="loading" @click="loadMore">
          <i v-if="loading" class="el-icon-loading"></i>
          <i v-else class="tim-icons icon-simple-add"></i>Load more
        </base-button>
      </div>
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="onClose">Close</base-button>
      </div>
    </template>

  </base-drawer>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, type PropType } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatPhoneDisplay } from '@/util/phoneFormatter'
import { reasonLabel } from '@/util/campaignSuppressionReasons'

const PER_PAGE = 50
const EXPORT_PER_PAGE = 100

const props = defineProps({
  show: { type: Boolean, default: false },
  // Either a saved audience id or a raw filter object; mirrors the lambda body
  audienceId: { type: Number as PropType<number | null>, default: null },
  audienceFilter: { type: Object as PropType<Record<string, any> | null>, default: null },
  // Ongoing campaign: list the reach (everyone who will be emailed between
  // today and endsOn), not today's slice. The caller passes the merged filter.
  reach: { type: Boolean, default: false },
  endsOn: { type: String, default: '' }
})

const emit = defineEmits(['update:show', 'close'])

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`

const customers = ref<any[]>([])
const total = ref(0)
const page = ref(1)
const loading = ref(false)
const error = ref('')

const downloading = ref(false)
// all / deliverable / undeliverable, mirrored to the lambda's mode
const mode = ref<'all' | 'deliverable' | 'undeliverable'>('all')

const hasMore = computed(() => customers.value.length < total.value)
const hasDownloadPermission = computed(() => {
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
  return permissions.some((p: string) => p === 'download_data')
})
const sectionTitle = computed(() => {
  if (!total.value) return props.reach ? 'Customers' : 'Recipients'
  const n = total.value.toLocaleString()
  if (mode.value === 'undeliverable') return `${n} undeliverable`
  if (mode.value === 'deliverable') return `${n} deliverable`
  return props.reach ? `${n} ${total.value === 1 ? 'customer' : 'customers'}, one email each on their day` : `${n} recipients`
})
const emptyText = computed(() => {
  if (mode.value === 'undeliverable') return 'No known undeliverable addresses in this audience'
  if (mode.value === 'deliverable') return 'No deliverable addresses in this audience'
  return 'No customers match this audience'
})

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function apiErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data
  if (data) {
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data
      if (parsed?.error) return parsed.error
    } catch {
      if (typeof data === 'string' && data.length < 200) return data
    }
  }
  return fallback
}

function displayName(customer: any): string {
  return `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.email || ''
}

function requestBody(nextPage: number, perPage: number): Record<string, any> {
  const body: Record<string, any> = { page: nextPage, per_page: perPage }
  if (mode.value !== 'all') body.mode = mode.value
  if (props.reach) {
    body.audience_filter = props.audienceFilter || {}
    body.estimate_reach = true
    body.ends_on = props.endsOn || null
  } else if (props.audienceId) body.audience_id = props.audienceId
  else body.audience_filter = props.audienceFilter || {}
  return body
}

async function fetchPage(nextPage: number) {
  loading.value = true
  error.value = ''
  try {
    const config = await authConfig()
    const response = await axios.post(`${baseUrl}/email-campaigns/audience-preview`, requestBody(nextPage, PER_PAGE), config)
    total.value = response.data.count || 0
    customers.value = nextPage === 1
      ? (response.data.customers || [])
      : [...customers.value, ...(response.data.customers || [])]
    page.value = nextPage
  } catch (err: any) {
    error.value = apiErrorMessage(err, 'Could not load the recipients')
  } finally {
    loading.value = false
  }
}

// Walks every page (100 per request, the lambda's cap) so the file holds the
// whole audience, not just what has been loaded into the drawer.
async function downloadCSV() {
  if (downloading.value) return
  downloading.value = true
  try {
    const config = await authConfig()
    const all: any[] = []
    let nextPage = 1
    while (true) {
      const response = await axios.post(`${baseUrl}/email-campaigns/audience-preview`, requestBody(nextPage, EXPORT_PER_PAGE), config)
      const rows = response.data.customers || []
      all.push(...rows)
      if (rows.length < EXPORT_PER_PAGE || all.length >= (response.data.count || 0)) break
      nextPage += 1
    }
    const esc = (v: any) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`
    let csv = 'First name,Last name,Email,Phone,Undeliverable\n'
    all.forEach((c) => {
      csv += [esc(c.first_name), esc(c.last_name), esc(c.email), esc(formatPhoneDisplay(c.phone, c.country || 'US')), esc(c.undeliverable_reason ? reasonLabel(c.undeliverable_reason) : '')].join(',') + '\n'
    })
    const anchor = document.createElement('a')
    anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    anchor.target = '_blank'
    anchor.download = mode.value === 'all' ? 'audience-recipients.csv' : `audience-${mode.value}.csv`
    anchor.click()
  } catch (err: any) {
    error.value = apiErrorMessage(err, 'Could not download the recipients')
  } finally {
    downloading.value = false
  }
}

function loadMore() {
  if (!loading.value && hasMore.value) fetchPage(page.value + 1)
}

function reset() {
  customers.value = []
  total.value = 0
  page.value = 1
  error.value = ''
}

function onClose() {
  emit('update:show', false)
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape') onClose()
}

watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    mode.value = 'all'
    reset()
    fetchPage(1)
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
})

watch(mode, () => {
  if (!props.show) return
  reset()
  fetchPage(1)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})
</script>

<script lang="ts">
export default { name: 'audience-customers-drawer' }
</script>

<style lang="scss" scoped>
// Layout only: one stacked row per recipient so long emails wrap instead of
// forcing a horizontal scroll inside the drawer
.recipient-row {
  padding: 10px 0;
  border-bottom: 1px solid rgba(125, 125, 125, 0.2);
  &:last-child { border-bottom: none; }
  &__name { display: block; font-weight: 600; }
  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    font-size: 0.8rem;
    overflow-wrap: anywhere;
    // secondary tier: text-white handles the light-mode flip, alpha keeps it a step under the name
    span { opacity: 0.85; }
    i { font-size: 0.75rem; margin-right: 6px; }
  }
  // the badge is a theme primitive; only its placement in the meta row is local
  &__flag { opacity: 1; align-self: center; }
}

.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
